/**
 * Contracts List Page
 * 
 * Main page component for displaying and managing contracts.
 */

import { renderContractList, initContractListHandlers } from '../../components/contracts/ContractList.js';
import { apiClient, getAuthToken, getApiBaseUrl } from '../../services/api.js';
import { t } from '../../i18n/translations.js';
import { toast } from '../../services/toast.js';

/**
 * Page state
 */
let state = {
  contracts: [],
  filters: {},
  pagination: { page: 1, totalPages: 1, totalItems: 0 },
  view: 'table',  // Default to table view like invoices page
  loading: true,
  error: null,
};

/**
 * Update page state and re-render
 */
function setState(newState) {
  state = { ...state, ...newState };
  render();
}

/**
 * Fetch contracts from API
 */
async function fetchContracts(page = 1, filters = {}) {
  setState({ loading: true, error: null });

  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: '20',
      ...filters,
    });

    console.log('[Contracts] Fetching from API...');
    const response = await apiClient.get(`/v1/contracts?${params}`);
    console.log('[Contracts] API response:', response);

    // Handle wrapped API response: { success, data: { success, data: [...], pageInfo } }
    // The API wraps responses twice, so we need to unwrap properly
    let data = response.data || response;
    // If there's another nested data object (double-wrapped), unwrap it
    if (data && data.data !== undefined) {
      data = data.data;
    }
    console.log('[Contracts] Unwrapped data:', data);
    const items = Array.isArray(data) ? data : (data.data || data.items || []);
    console.log('[Contracts] Items:', items);
    const pageInfo = (typeof data === 'object' && !Array.isArray(data)) ? (data.pageInfo || {}) : {};

    setState({
      contracts: items,
      pagination: {
        page: pageInfo.page || page,
        totalPages: pageInfo.totalPages || 1,
        totalItems: pageInfo.totalItems || items.length || 0,
      },
      filters,
      loading: false,
    });
  } catch (err) {
    console.error('[Contracts] Failed to fetch contracts:', err);
    setState({
      contracts: [],
      pagination: {
        page: 1,
        totalPages: 1,
        totalItems: 0,
      },
      loading: false,
      error: err.message || 'Failed to load contracts',
    });
  }
}


/**
 * Initialize global handlers
 */
function initHandlers() {
  // View handlers
  window.setContractView = (view) => {
    setState({ view });
  };

  // Pagination handlers
  window.goToContractPage = (page) => {
    fetchContracts(page, state.filters);
  };

  // Filter handlers
  window.applyContractFilters = () => {
    const status = document.getElementById('filter-status')?.value || '';
    const expiringDays = document.getElementById('filter-expiring')?.value || '';
    const vendor = document.getElementById('filter-vendor')?.value || '';

    const filters = {};
    if (status) filters.status = status;

    // Convert expiringDays to expiringBefore date
    if (expiringDays) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + parseInt(expiringDays, 10));
      filters.expiringBefore = futureDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    }

    // Send vendor as vendorName for backend to search
    if (vendor) filters.vendorName = vendor;

    fetchContracts(1, filters);
  };

  window.clearContractFilters = () => {
    fetchContracts(1, {});
  };

  // Action handlers
  window.viewContract = (id) => {
    window.location.hash = `contracts/${id}`;
  };

  window.editContract = (id) => {
    window.location.hash = `contracts/${id}?edit=true`;
  };

  window.downloadContract = async (id) => {
    try {
      const token = getAuthToken();
      const downloadUrl = `${getApiBaseUrl()}/v1/contracts/${id}/download`;

      // Use fetch to get the file with auth header
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(error.detail || 'Download failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `contract_${id}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Failed to download contract:', err);
      toast.error(t('contracts.downloadFailed') || 'Failed to download contract document: ' + (err.message || 'Unknown error'));
    }
  };

  window.renewContract = (id) => {
    console.log('Renew contract:', id);
    // Transition to renewed status
    window.transitionContract(id, 'renewed');
  };

  // Toggle action menu visibility
  window.toggleContractActions = (event, id) => {
    event.stopPropagation();

    // Close all other open menus
    document.querySelectorAll('.action-menu.open').forEach(menu => {
      if (menu.dataset.contractId !== id) {
        menu.classList.remove('open');
      }
    });

    // Toggle the clicked menu
    const menu = document.querySelector(`.action-menu[data-contract-id="${id}"]`);
    if (menu) {
      const isOpen = menu.classList.contains('open');

      if (!isOpen) {
        // Position the menu relative to the button
        const button = event.currentTarget;
        const rect = button.getBoundingClientRect();

        // Position below the button, aligned to the right
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.left = 'auto';
        menu.style.right = `${window.innerWidth - rect.right}px`;

        // Check if menu would go off-screen at the bottom
        menu.classList.add('open');
        const menuRect = menu.getBoundingClientRect();
        if (menuRect.bottom > window.innerHeight) {
          // Position above the button instead
          menu.style.top = `${rect.top - menuRect.height - 4}px`;
        }
      } else {
        menu.classList.remove('open');
      }
    }
  };

  // Close action menus when clicking outside
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.action-menu-wrapper')) {
      document.querySelectorAll('.action-menu.open').forEach(menu => {
        menu.classList.remove('open');
      });
    }
  });

  // Transition contract status
  window.transitionContract = async (id, newStatus) => {
    // Close the action menu
    document.querySelectorAll('.action-menu.open').forEach(menu => {
      menu.classList.remove('open');
    });

    const statusLabels = {
      pending_review: 'Pending Review',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      active: 'Active',
      terminated: 'Terminated',
      renewed: 'Renewed',
      draft: 'Draft',
    };

    const confirmMessage = `Are you sure you want to change the contract status to "${statusLabels[newStatus] || newStatus}"?`;
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      console.log(`Transitioning contract ${id} to ${newStatus}...`);
      const response = await apiClient.post(`/v1/contracts/${id}/transition`, {
        newStatus: newStatus,
      });

      console.log('Transition response:', response);
      toast.success(t('contracts.statusUpdated') || `Contract status updated to ${statusLabels[newStatus] || newStatus}`);

      // Refresh the contracts list
      await fetchContracts(state.pagination.page, state.filters);
    } catch (err) {
      console.error('Failed to transition contract:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      toast.error(t('contracts.statusUpdateFailed') || `Failed to update contract status: ${errorMessage}`);
    }
  };

  // Delete contract
  window.deleteContract = async (id) => {
    // Close the action menu
    document.querySelectorAll('.action-menu.open').forEach(menu => {
      menu.classList.remove('open');
    });

    if (!confirm('Are you sure you want to delete this contract? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/v1/contracts/${id}`);
      toast.success(t('contracts.deleteSuccess') || 'Contract deleted successfully');

      // Refresh the contracts list
      await fetchContracts(state.pagination.page, state.filters);
    } catch (err) {
      console.error('Failed to delete contract:', err);
      toast.error(t('contracts.deleteFailed') || 'Failed to delete contract. Please try again.');
    }
  };

  // Initialize component handlers
  initContractListHandlers();
}

/**
 * Render the page
 */
function render() {
  const container = document.getElementById('contracts-page') || document.getElementById('app-content');
  if (!container) return;

  if (state.loading) {
    container.innerHTML = `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="page contracts-page">
      ${renderContractList({
        contracts: state.contracts,
        filters: state.filters,
        pagination: state.pagination,
        view: state.view,
        showFilters: true,
      })}
    </div>
  `;
}

/**
 * Page component export
 */
export default {
  /**
   * Initialize the page
   */
  async init() {
    initHandlers();
    await fetchContracts();
  },

  /**
   * Render the page content
   */
  render() {
    return `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
  },

  /**
   * Called after page is mounted
   */
  async mounted() {
    await this.init();
  },

  /**
   * Cleanup on page leave
   */
  destroy() {
    delete window.setContractView;
    delete window.goToContractPage;
    delete window.applyContractFilters;
    delete window.clearContractFilters;
    delete window.viewContract;
    delete window.editContract;
    delete window.downloadContract;
    delete window.renewContract;
    delete window.toggleContractActions;
    delete window.transitionContract;
    delete window.deleteContract;
  },
};
